import { Box, Card, Chip, CircularProgress, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { H6 } from 'components/Typography';
import { TFunction } from 'i18next';
import { FC, useState } from 'react';
import styled from '@emotion/styled';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { differenceInDays } from 'date-fns';
import CalendarInput from 'components/input-fields/CalendarInput';
import { contractsApi } from 'api';
import { UpdateContractDto, UpdateContractDtoLegalContractTypeEnum } from 'api/generated';

const CONTRACT_TYPES = ['LIMITED', 'UNLIMITED', 'INTERNSHIP', 'CALL', 'APPRENTICESHIP'] as const;

const getContractTypeColor = (type: string) => {
  switch (type) {
    case 'LIMITED':
      return '#FFD700';
    case 'UNLIMITED':
      return '#DE3163';
    case 'INTERNSHIP':
      return '#7FFFD4';
    case 'APPRENTICESHIP':
      return '#DA70D6';
    case 'CALL':
      return '#3CB371';
    default:
      return '#E0E0E0';
  }
};

const getTextColorBasedOnBgColor = (bgColor: string) => {
  const color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#FFFFFF';
};

const ContractTypeChip = styled(Chip)(({ theme, contractType }: { theme: Theme; contractType: string }) => ({
  height: 24,
  borderRadius: 12,
  backgroundColor: getContractTypeColor(contractType),
  color: getTextColorBasedOnBgColor(getContractTypeColor(contractType)),
  fontWeight: 600,
  '& .MuiChip-label': {
    fontSize: '0.75rem',
    padding: '0 10px',
  },
}));

const InfoBox = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 10px',
  backgroundColor: theme.palette.background.default,
  borderRadius: '6px',
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
  },
}));

interface DataProps {
  realContractStartDate?: Date,
  selectedDate: Date,
  contract: {
    startDate: string;
    endDate: string;
    legalContractType?: 'LIMITED' | 'UNLIMITED' | 'INTERNSHIP' | 'CALL' | 'APPRENTICESHIP';
  };
  contractId?: number;
  onUpdate?: () => void;
  t: TFunction;
}

const EmployeeContract: FC<DataProps> = (props) => {
  const { t, contractId, onUpdate } = props;
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editStartDate, setEditStartDate] = useState<Date | null>(null);
  const [editEndDate, setEditEndDate] = useState<Date | null>(null);
  const [editContractType, setEditContractType] = useState<string>('');
  const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(null);

  // Helper function to parse DD/MM/YYYY format dates
  const formatDateFromIT = (date: string) => {
    if (!date) return null;
    const formattedDate = date.split('/');
    if (formattedDate.length !== 3) return null;
    const startUTCDate = Date.UTC(Number(formattedDate[2]), Number(formattedDate[1]) - 1, Number(formattedDate[0]));
    return new Date(startUTCDate);
  };

  const enterEditMode = () => {
    setEditStartDate(formatDateFromIT(props.contract.startDate));
    setEditEndDate(formatDateFromIT(props.contract.endDate));
    setEditContractType(props.contract.legalContractType || 'LIMITED');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditStartDate(null);
    setEditEndDate(null);
    setEditContractType('');
  };

  const handleSave = async () => {
    if (!contractId) return;
    setIsSaving(true);
    try {
      const updateDto: UpdateContractDto = {};
      if (editStartDate) {
        updateDto.startedOn = new Date(Date.UTC(editStartDate.getFullYear(), editStartDate.getMonth(), editStartDate.getDate())).toISOString();
      }
      if (editContractType !== 'UNLIMITED' && editEndDate) {
        updateDto.endedOn = new Date(Date.UTC(editEndDate.getFullYear(), editEndDate.getMonth(), editEndDate.getDate())).toISOString();
      }
      if (editContractType === 'UNLIMITED') {
        updateDto.endedOn = null;
      }
      if (editContractType && editContractType !== props.contract.legalContractType) {
        updateDto.legalContractType = editContractType as UpdateContractDtoLegalContractTypeEnum;
      }
      await contractsApi.update(contractId.toString(), updateDto);
      setIsEditing(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update contract:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const calculateCalendarDays = () => {
    if (!props.contract.startDate) return null;

    const startDate = formatDateFromIT(props.contract.startDate);

    // Validate start date
    if (!startDate || isNaN(startDate.getTime())) return null;

    let endDate: Date;

    // Determine end date based on contract type
    if (props.contract.legalContractType === 'UNLIMITED') {
      // For unlimited contracts, use selected date
      endDate = props.selectedDate;
    } else if (props.contract.endDate) {
      // For limited contracts, try to use end date
      const parsedEndDate = formatDateFromIT(props.contract.endDate);
      if (parsedEndDate && !isNaN(parsedEndDate.getTime())) {
        endDate = parsedEndDate;
      } else {
        endDate = props.selectedDate;
      }
    } else {
      // No end date provided, use selected date
      endDate = props.selectedDate;
    }

    // Final validation
    if (isNaN(endDate.getTime())) return null;

    // Use date-fns for accurate date calculation
    // differenceInDays calculates "from and including start date to but not including end date"
    const daysDifference = differenceInDays(endDate, startDate) + 1;

    return Math.max(0, daysDifference);
  };

  const getContractTypeTranslation = (type: string) => {
    switch (type) {
      case 'LIMITED':
        return t('employees.contract.types.limited');
      case 'UNLIMITED':
        return t('employees.contract.types.unlimited');
      case 'INTERNSHIP':
        return t('employees.contract.types.internship');
      case 'CALL':
        return t('employees.contract.types.call');
      case 'APPRENTICESHIP':
        return t('employees.contract.types.apprenticeship');
      default:
        return type;
    }
  };

  const currentContractType = isEditing ? editContractType : props.contract.legalContractType;

  return (
    <Card sx={{ padding: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <H6>{t('employees.contract.title')}</H6>
        <Box display="flex" alignItems="center" gap={1}>
          {isEditing ? (
            <>
              <Tooltip title={t('cancel')}>
                <IconButton size="small" onClick={cancelEdit} disabled={isSaving}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('save')}>
                <IconButton size="small" onClick={handleSave} disabled={isSaving} color="primary">
                  {isSaving ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </>
          ) : (
            contractId && (
              <Tooltip title={t('edit')}>
                <IconButton size="small" onClick={enterEditMode}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
          )}
          {currentContractType && (
            <>
              <ContractTypeChip
                theme={theme}
                contractType={currentContractType}
                label={getContractTypeTranslation(currentContractType)}
                onClick={isEditing ? (e) => setTypeMenuAnchor(e.currentTarget) : undefined}
                sx={isEditing ? { cursor: 'pointer', '&:hover': { opacity: 0.8 } } : {}}
              />
              {isEditing && (
                <Menu
                  anchorEl={typeMenuAnchor}
                  open={Boolean(typeMenuAnchor)}
                  onClose={() => setTypeMenuAnchor(null)}
                >
                  {CONTRACT_TYPES.map((type) => (
                    <MenuItem
                      key={type}
                      selected={type === editContractType}
                      onClick={() => {
                        setEditContractType(type);
                        if (type === 'UNLIMITED') {
                          setEditEndDate(null);
                        }
                        setTypeMenuAnchor(null);
                      }}
                    >
                      <Chip
                        size="small"
                        label={getContractTypeTranslation(type)}
                        sx={{
                          backgroundColor: getContractTypeColor(type),
                          color: getTextColorBasedOnBgColor(getContractTypeColor(type)),
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={currentContractType === 'UNLIMITED' ? 6 : 4}>
          {isEditing ? (
            <CalendarInput
              format="dd/MM/yyyy"
              label={t('employees.contract.startingDate')}
              value={editStartDate}
              onChange={(newValue) => {
                if (newValue && !isNaN(new Date(newValue).getTime())) {
                  setEditStartDate(newValue);
                }
              }}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          ) : (
            <InfoBox theme={theme}>
              <CalendarTodayIcon />
              <Box>
                <Typography color="text.secondary" fontSize="11px">
                  {t('employees.contract.startingDate')}
                </Typography>
                <Typography fontWeight="600" fontSize="13px">
                  {props.contract.startDate}
                </Typography>
              </Box>
            </InfoBox>
          )}
        </Grid>

        {currentContractType !== 'UNLIMITED' && (
          <Grid item xs={12} sm={4}>
            {isEditing ? (
              <CalendarInput
                format="dd/MM/yyyy"
                label={t('employees.contract.expiryDate')}
                value={editEndDate}
                minDate={editStartDate || undefined}
                onChange={(newValue) => {
                  if (newValue && !isNaN(new Date(newValue).getTime())) {
                    setEditEndDate(newValue);
                  }
                }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            ) : (
              <InfoBox theme={theme}>
                <EventIcon />
                <Box>
                  <Typography color="text.secondary" fontSize="11px">
                    {t('employees.contract.expiryDate')}
                  </Typography>
                  <Typography fontWeight="600" fontSize="13px">
                    {props.contract.endDate}
                  </Typography>
                </Box>
              </InfoBox>
            )}
          </Grid>
        )}

        <Grid item xs={12} sm={currentContractType === 'UNLIMITED' ? 6 : 4}>
          <InfoBox theme={theme}>
            <AccessTimeIcon />
            <Box>
              <Typography color="text.secondary" fontSize="11px">
                {currentContractType === 'UNLIMITED'
                  ? t('employees.contract.daysActive')
                  : t('employees.contract.totalDays')
                }
              </Typography>
              <Typography fontWeight="600" fontSize="13px" color="primary.main">
                {calculateCalendarDays() !== null
                  ? `${calculateCalendarDays()?.toLocaleString()} ${t('employees.contract.days')}`
                  : '-- --'
                }
              </Typography>
            </Box>
          </InfoBox>
        </Grid>
      </Grid>
    </Card>
  );
};

export default EmployeeContract;